/**
 * The office key — how the admin page authenticates, and nothing else.
 *
 * ── Why there is a credential on this screen at all ──────────────────────────
 * D-005 is emphatic: Chip and Tim never see, type or handle a TOKEN. That is
 * about the surveyor access token, the thing minted per assignment and texted
 * out — and this file has nothing to do with it. The office key is the API's
 * OTHER principal, `ADMIN_TOKEN`, the shared secret every `/api/surveyors` and
 * `/api/assignments` route is already gated on in functions/api/[[route]].ts.
 * Some secret has to reach those routes; the API defines exactly one, and
 * inventing a second auth mechanism in the UI would be duplicating auth logic
 * that already exists.
 *
 * ── sessionStorage, not localStorage ─────────────────────────────────────────
 * Deliberate, and the opposite of the surveyor's token. The surveyor's token
 * lives in localStorage because their whole job is to keep working offline for
 * days after the link arrives. The office key backs a screen that only works
 * online, on an office machine, in a browser tab — so it expires with the tab.
 * A shared desktop does not keep the roster unlocked overnight.
 *
 * It is written, sent as a bearer header, and never read back into the DOM.
 * `hasOfficeKey()` exists precisely so a screen can ask "are we signed in?"
 * without the value passing through a component.
 */

const OFFICE_KEY = 'upsurv.officeKey';

function session(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    // Safari in Lockdown / private mode throws on the property access itself.
    return null;
  }
}

/**
 * The key, for the ONE caller that may have it: `AdminClient`, on its way into
 * an Authorization header. No component calls this.
 */
export function readOfficeKey(): string | null {
  try {
    const value = session()?.getItem(OFFICE_KEY) ?? '';
    return value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

/** Whether the office is signed in. The safe question for a component to ask. */
export function hasOfficeKey(): boolean {
  return readOfficeKey() !== null;
}

/** Returns false when storage refused it — the screen must not claim success. */
export function writeOfficeKey(key: string): boolean {
  const trimmed = key.trim();
  if (trimmed.length === 0) return false;
  try {
    session()?.setItem(OFFICE_KEY, trimmed);
    return readOfficeKey() === trimmed;
  } catch {
    return false;
  }
}

export function clearOfficeKey(): void {
  try {
    session()?.removeItem(OFFICE_KEY);
  } catch {
    /* best effort */
  }
}
